// FE-PINV-065 — <SavedViewSwitcher>.
//
// Dropdown anchor labeled with the active view's name (default
// "My pipeline"). Stack icon opens the full list; each row has a
// pin affordance + kebab (Rename / Save changes / Save as new /
// Delete).
//
// State contract:
//   - Host (W1's shell) owns ``activeViewId`` + the current
//     ``PipelineFilters``. The switcher fires ``onSelect`` when the
//     producer picks a row; the host updates URL + view state.
//   - On first mount with ``activeViewId === null``, the switcher
//     fires ``onSelect`` with the pinned-default view so the host
//     can hydrate without needing a separate "default" round-trip.
//   - When current filters diverge from the active view's
//     persisted filters_json, the modified affordance surfaces.

import {
  useEffect, useMemo, useRef, useState,
} from "react";

import { Button, Modal } from "@/design-system";

import { CrmFormBanner } from "../components/CrmFormBanner";
import {
  isFiltersEmpty, type PipelineFilters,
} from "./filterState";
import {
  useCreateSavedViewMutation,
  useDeleteSavedViewMutation,
  usePatchSavedViewMutation,
  usePinSavedViewMutation,
  useSavedViewsQuery,
} from "./savedViewHooks";
import type { SavedView } from "./savedViewTypes";


export interface SavedViewSwitcherProps {
  activeViewId: string | null;
  filters: PipelineFilters;
  onSelect: (view: SavedView) => void;
  /** Called when the producer hits Save in the Save-view modal as a
   *  fork. Host can use this for telemetry/Toast; mutation lifecycle
   *  is already owned by the switcher. */
  onSaveCurrentAs?: (name: string, pinAsDefault: boolean) => void;
}


export function SavedViewSwitcher({
  activeViewId, filters, onSelect, onSaveCurrentAs,
}: SavedViewSwitcherProps): JSX.Element {
  const listQ = useSavedViewsQuery();
  const rows = listQ.data?.rows ?? [];
  const pinnedDefault = rows.find((r) => r.is_pinned_default) ?? rows[0];

  // Hydrate the host on first mount when no view is active. We do
  // this once and only once per pinned-default change.
  const firedDefaultRef = useRef<string | null>(null);
  useEffect(() => {
    if (activeViewId !== null) return;
    if (!pinnedDefault) return;
    if (firedDefaultRef.current === pinnedDefault.id) return;
    firedDefaultRef.current = pinnedDefault.id;
    onSelect(pinnedDefault);
  }, [activeViewId, pinnedDefault, onSelect]);

  const activeView = useMemo(() => {
    if (activeViewId === null) return pinnedDefault ?? null;
    return rows.find((r) => r.id === activeViewId) ?? pinnedDefault ?? null;
  }, [activeViewId, rows, pinnedDefault]);

  const [listOpen, setListOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);

  const modified = useMemo(() => {
    if (!activeView) return false;
    return !sameFilters(activeView.filters_json, filters);
  }, [activeView, filters]);

  return (
    <div className="relative inline-flex items-center gap-2">
      <button
        type="button"
        data-testid="saved-view-anchor"
        onClick={() => setListOpen((o) => !o)}
        className="inline-flex items-center gap-1 rounded-md border border-border-default bg-raised px-2 py-1 text-sm font-medium text-text-primary hover:border-text-tertiary"
      >
        <span>{activeView?.name ?? "My pipeline"}</span>
        <span aria-hidden="true" className="text-text-tertiary">▾</span>
      </button>

      {modified ? (
        <button
          type="button"
          data-testid="saved-view-modified-affordance"
          onClick={() => setSaveOpen(true)}
          className="rounded border border-amber bg-amber-tint px-2 py-0.5 text-xs text-text-primary"
          title="Save changes to this view"
        >
          • Save changes
        </button>
      ) : null}

      {listOpen ? (
        <SavedViewList
          rows={rows}
          activeViewId={activeView?.id ?? null}
          pinnedDefaultId={pinnedDefault?.id ?? null}
          onSelect={(view) => {
            setListOpen(false);
            onSelect(view);
          }}
          onRequestSaveAs={() => {
            setListOpen(false);
            setSaveOpen(true);
          }}
          onActiveViewDeleted={() => {
            if (pinnedDefault) onSelect(pinnedDefault);
          }}
        />
      ) : null}

      {saveOpen && activeView ? (
        <SaveViewModal
          activeView={activeView}
          filters={filters}
          onClose={() => setSaveOpen(false)}
          onSaved={(name, pinAsDefault) => {
            onSaveCurrentAs?.(name, pinAsDefault);
            setSaveOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}


// ── Row list ──────────────────────────────────────────────────────


function SavedViewList({
  rows, activeViewId, pinnedDefaultId,
  onSelect, onRequestSaveAs, onActiveViewDeleted,
}: {
  rows: SavedView[];
  activeViewId: string | null;
  pinnedDefaultId: string | null;
  onSelect: (view: SavedView) => void;
  onRequestSaveAs: () => void;
  onActiveViewDeleted: () => void;
}) {
  const pin = usePinSavedViewMutation();
  const del = useDeleteSavedViewMutation();
  const patch = usePatchSavedViewMutation();
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameText, setRenameText] = useState("");

  const sorted = useMemo(
    () => [...rows].sort((a, b) => {
      if (a.is_pinned_default && !b.is_pinned_default) return -1;
      if (!a.is_pinned_default && b.is_pinned_default) return 1;
      return a.name.localeCompare(b.name);
    }),
    [rows],
  );

  return (
    <ul
      data-testid="saved-view-list"
      className="absolute left-0 top-full z-30 mt-1 flex w-72 flex-col rounded-md border border-border-default bg-raised shadow-overlay"
    >
      {sorted.map((row) => {
        const isPinned = row.id === pinnedDefaultId;
        return (
          <li
            key={row.id}
            className="flex items-center gap-1 border-b border-border-subtle px-2 py-1 last:border-none"
          >
            <button
              type="button"
              onClick={() => onSelect(row)}
              className={[
                "flex-1 truncate rounded px-1 py-1 text-left text-sm",
                row.id === activeViewId
                  ? "bg-teal-tint text-text-primary"
                  : "text-text-primary hover:bg-recessed",
              ].join(" ")}
            >
              {row.name}
              {isPinned ? (
                <span className="ml-1 text-[10px] uppercase tracking-wide text-text-tertiary">
                  default
                </span>
              ) : null}
            </button>
            {isPinned ? (
              <span
                aria-label="Pinned as default"
                title="Pinned as default"
                className="p-1 text-xs text-text-tertiary"
              >
                ★
              </span>
            ) : (
              <button
                type="button"
                aria-label="Pin as default"
                disabled={pin.isPending}
                onClick={() => pin.mutate(row.id)}
                title="Pin as default"
                className="rounded p-1 text-xs text-text-secondary hover:bg-recessed"
              >
                ★
              </button>
            )}
            <RowActionsKebab
              row={row}
              onRename={(name) => {
                setRenamingId(row.id);
                setRenameText(name);
              }}
              onSaveAs={onRequestSaveAs}
              onDelete={() => setConfirmDeleteId(row.id)}
            />
          </li>
        );
      })}

      {renamingId !== null ? (
        <RenameModal
          name={renameText}
          onChange={setRenameText}
          onClose={() => setRenamingId(null)}
          onSubmit={() => {
            patch.mutate(
              { id: renamingId, patch: { name: renameText } },
              { onSuccess: () => setRenamingId(null) },
            );
          }}
        />
      ) : null}

      {confirmDeleteId !== null ? (
        <ConfirmDeleteModal
          row={rows.find((r) => r.id === confirmDeleteId) ?? null}
          onClose={() => setConfirmDeleteId(null)}
          onConfirm={() => {
            const id = confirmDeleteId;
            del.mutate(id, {
              onSuccess: () => {
                setConfirmDeleteId(null);
                if (id === activeViewId) onActiveViewDeleted();
              },
            });
          }}
        />
      ) : null}
    </ul>
  );
}


function RowActionsKebab({
  row, onRename, onSaveAs, onDelete,
}: {
  row: SavedView;
  onRename: (currentName: string) => void;
  onSaveAs: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        aria-label="Open row actions"
        onClick={() => setOpen((o) => !o)}
        className="rounded p-1 text-text-tertiary hover:bg-recessed"
      >
        ⋮
      </button>
      {open ? (
        <ul className="absolute right-0 top-full z-40 mt-1 w-48 rounded-md border border-border-default bg-raised shadow-overlay">
          <li>
            <button
              type="button"
              onClick={() => { setOpen(false); onRename(row.name); }}
              className="block w-full px-2 py-1 text-left text-xs text-text-primary hover:bg-recessed"
            >
              Rename
            </button>
          </li>
          <li>
            <button
              type="button"
              onClick={() => { setOpen(false); onSaveAs(); }}
              className="block w-full px-2 py-1 text-left text-xs text-text-primary hover:bg-recessed"
            >
              Save changes to this view
            </button>
          </li>
          <li>
            <button
              type="button"
              onClick={() => { setOpen(false); onSaveAs(); }}
              className="block w-full px-2 py-1 text-left text-xs text-text-primary hover:bg-recessed"
            >
              Save as new view
            </button>
          </li>
          <li>
            <button
              type="button"
              onClick={() => { setOpen(false); onDelete(); }}
              className="block w-full px-2 py-1 text-left text-xs text-text-primary hover:bg-recessed"
            >
              Delete
            </button>
          </li>
        </ul>
      ) : null}
    </div>
  );
}


// ── Save view modal ───────────────────────────────────────────────


function SaveViewModal({
  activeView, filters, onClose, onSaved,
}: {
  activeView: SavedView;
  filters: PipelineFilters;
  onClose: () => void;
  onSaved: (name: string, pinAsDefault: boolean) => void;
}) {
  const create = useCreateSavedViewMutation();
  const patch = usePatchSavedViewMutation();
  const [name, setName] = useState(activeView.name);
  const [pinAsDefault, setPinAsDefault] = useState(
    activeView.is_pinned_default,
  );
  const [bannerErrors, setBannerErrors] = useState<{ message: string }[]>([]);

  const isFork = name !== activeView.name;

  function submit() {
    setBannerErrors([]);
    if (isFork) {
      create.mutate(
        { name, filters_json: filters, is_pinned_default: pinAsDefault },
        {
          onSuccess: () => onSaved(name, pinAsDefault),
          onError: (err) => surfaceError(err, setBannerErrors),
        },
      );
      return;
    }
    patch.mutate(
      {
        id: activeView.id,
        patch: { name, filters_json: filters, is_pinned_default: pinAsDefault },
      },
      {
        onSuccess: () => onSaved(name, pinAsDefault),
        onError: (err) => surfaceError(err, setBannerErrors),
      },
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={isFork ? "Save as new view" : "Save view"}
      size="sm"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={submit}
            data-testid="save-view-submit"
          >
            Save
          </Button>
        </div>
      }
    >
      <div data-testid="save-view-modal" className="flex flex-col gap-3">
        <CrmFormBanner errors={bannerErrors} />
        <label className="flex flex-col gap-1 text-xs text-text-secondary">
          Name
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="rounded border border-border-default bg-raised px-2 py-1 text-sm text-text-primary"
          />
        </label>
        <label className="inline-flex items-center gap-2 text-xs text-text-secondary">
          <input
            type="checkbox"
            checked={pinAsDefault}
            onChange={() => setPinAsDefault((v) => !v)}
          />
          Pin as default
        </label>
      </div>
    </Modal>
  );
}


function surfaceError(
  err: unknown,
  setBannerErrors: (errs: { message: string }[]) => void,
) {
  const detail = (err as { body?: { detail?: string } }).body?.detail;
  if (detail === "duplicate_name" || (err as { status?: number }).status === 409) {
    setBannerErrors([{
      message: "A saved view with that name already exists for you.",
    }]);
    return;
  }
  setBannerErrors([{
    message: err instanceof Error ? err.message : "Could not save view.",
  }]);
}


function RenameModal({
  name, onChange, onClose, onSubmit,
}: {
  name: string;
  onChange: (next: string) => void;
  onClose: () => void;
  onSubmit: () => void;
}) {
  return (
    <Modal
      open
      onClose={onClose}
      title="Rename saved view"
      size="sm"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={onSubmit}>Save</Button>
        </div>
      }
    >
      <label className="flex flex-col gap-1 text-xs text-text-secondary">
        Name
        <input
          type="text"
          value={name}
          onChange={(e) => onChange(e.target.value)}
          className="rounded border border-border-default bg-raised px-2 py-1 text-sm text-text-primary"
        />
      </label>
    </Modal>
  );
}


function ConfirmDeleteModal({
  row, onClose, onConfirm,
}: {
  row: SavedView | null;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal
      open
      onClose={onClose}
      title="Delete saved view?"
      size="sm"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={onConfirm}
            aria-label="Confirm delete"
          >
            Delete
          </Button>
        </div>
      }
    >
      <p className="text-sm text-text-secondary">
        {row
          ? `Delete "${row.name}"? This cannot be undone.`
          : "Delete this view?"}
      </p>
    </Modal>
  );
}


// ── Filter equality ───────────────────────────────────────────────


function sameFilters(a: PipelineFilters, b: PipelineFilters): boolean {
  if (isFiltersEmpty(a) && isFiltersEmpty(b)) return true;
  const sameSet = <T extends string>(x: T[], y: T[]) => {
    if (x.length !== y.length) return false;
    const sx = new Set<string>(x);
    return y.every((v) => sx.has(v));
  };
  return (
    sameSet(a.cadence, b.cadence)
    && sameSet(a.stage, b.stage)
    && sameSet(a.lives_band, b.lives_band)
    && a.renewal_window === b.renewal_window
    && sameSet(a.opportunity_type, b.opportunity_type)
    && sameSet(a.producer, b.producer)
    && a.alerts_only === b.alerts_only
  );
}
